/**
 * Opportunity API
 *
 * Read-only Dataverse API for Dynamics CRM Opportunities.
 * Includes broker, group, owner, and sales process fields.
 */

import {
  dataverseClient,
  type QueryOptions,
} from '@shared/api';
import type { DataverseEntity } from '@shared/api/dataverse/types';
import { escapeODataString } from '@shared/api/dataverse/utils';
import { assertGuid } from '@shared/lib';

/** Reject anything that is not an ISO date or datetime literal — guards date filters from injection. */
function assertDateLiteral(value: string, label: string): string {
  if (!/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?Z?)?$/.test(value)) {
    throw new Error(`Invalid date literal for ${label}: ${value}`);
  }
  return value;
}
import type { Opportunity, OpportunityQueryParams, OppStateCode } from '../model/types';
// Value import is safe: resolveClose imports from this module with `import type` only.
import { resolveCloseRecord } from '../lib/resolveClose';
import {
  OPP_CLOSE_SELECT,
  OPP_HAS_CLOSE_DATA_FILTER,
  oppCloseFields,
  overlayOppClose,
  type OppCloseEntity,
  type OppCloseFields,
} from '../lib/oppCloseFields';

// =============================================================================
// Dataverse Entity Shape
// =============================================================================

interface OpportunityEntity extends DataverseEntity {
  opportunityid: string;
  name: string;
  // Lookups
  _parentcontactid_value?: string;
  '_parentcontactid_value@OData.Community.Display.V1.FormattedValue'?: string;
  _customerid_value?: string;
  '_customerid_value@OData.Community.Display.V1.FormattedValue'?: string;
  _parentaccountid_value?: string;
  '_parentaccountid_value@OData.Community.Display.V1.FormattedValue'?: string;
  _ownerid_value?: string;
  '_ownerid_value@OData.Community.Display.V1.FormattedValue'?: string;
  // Status
  statecode: number;
  'statecode@OData.Community.Display.V1.FormattedValue'?: string;
  statuscode?: number;
  'statuscode@OData.Community.Display.V1.FormattedValue'?: string;
  // Sales process
  stepname?: string;
  // Rating
  opportunityratingcode?: number;
  'opportunityratingcode@OData.Community.Display.V1.FormattedValue'?: string;
  // Dates
  estimatedclosedate?: string;
  actualclosedate?: string;
  dts_effectivedate?: string;
  createdon?: string;
  modifiedon?: string;
  // Custom fields
  dts_numberofemployees?: number;
  dts_numberofmembers?: number;
  dts_howinsured?: string;
  'dts_howinsured@OData.Community.Display.V1.FormattedValue'?: string;
  // Description
  description?: string;
  // Presentation status
  new_presentationstatus?: number;
  'new_presentationstatus@OData.Community.Display.V1.FormattedValue'?: string;
  // Win factors (Two Option — the Web API returns JSON booleans; older code
  // assumed 0/1 numbers, so the mapper accepts both)
  dts_brokerofrecord?: boolean | number | null;
  dts_hasveracitywonbusinessfrombroker?: boolean | number | null;
  dts_hasbrokereverplacedbusinesswithindependentt?: boolean | number | null;
  // Renewal (Two Option — same boolean wire format)
  dts_renewal?: boolean | number | null;
  // Current-vendor pickers (may be Choice with FormattedValue or raw string)
  dts_currenttpa?: string | number | null;
  'dts_currenttpa@OData.Community.Display.V1.FormattedValue'?: string;
  dts_currentnetwork?: string | number | null;
  'dts_currentnetwork@OData.Community.Display.V1.FormattedValue'?: string;
  dts_currentpbm?: string | number | null;
  'dts_currentpbm@OData.Community.Display.V1.FormattedValue'?: string;
  dts_currentstoploss?: string | number | null;
  'dts_currentstoploss@OData.Community.Display.V1.FormattedValue'?: string;
  dts_brokercommissiontype?: string | number | null;
  'dts_brokercommissiontype@OData.Community.Display.V1.FormattedValue'?: string;
  // Product toggles (Products to Quote)
  dts_tpaadmin?: boolean;
  dts_veracityrx?: boolean;
  dts_veracityonsite?: boolean;
  dts_veracityrisk?: boolean;
  dts_providersofexcellence?: boolean;
  dts_minimumessentialcoveragemec?: boolean;
  dts_maplebridge?: boolean;
  // Two more product toggles that only the Lost Opportunities report asks for
  // (ADO #1449). They are NOT in SELECT_FIELDS: see LOST_EXTRA_SELECT below.
  new_caresolutions?: boolean;
  dts_healthplansolutions?: boolean;
  // Group
  _dts_group_value?: string;
  '_dts_group_value@OData.Community.Display.V1.FormattedValue'?: string;
}

// =============================================================================
// Entity Mapping
// =============================================================================

const PRODUCT_TOGGLE_MAP: Record<string, string> = {
  dts_tpaadmin: 'TPA Admin',
  dts_veracityrx: 'Veracity Rx',
  dts_veracityonsite: 'Veracity OnSite',
  dts_veracityrisk: 'Veracity Risk',
  dts_providersofexcellence: 'Providers of Excellence',
  dts_minimumessentialcoveragemec: 'MEC',
  dts_maplebridge: 'Maplebridge',
  // Only populated when the caller selected LOST_EXTRA_SELECT; absent fields
  // are simply skipped by buildProductsToQuote, so every other page is
  // unaffected by these two entries.
  new_caresolutions: 'Care Solutions',
  dts_healthplansolutions: 'Health Plan Solutions',
};

function buildProductsToQuote(entity: OpportunityEntity): string[] {
  const products: string[] = [];
  for (const [field, label] of Object.entries(PRODUCT_TOGGLE_MAP)) {
    if (entity[field] === true) products.push(label);
  }
  return products;
}

/** Two Option fields arrive as JSON booleans from the Web API (0/1 numbers in
 *  some legacy paths). Accept both; anything else (null/undefined) means the
 *  field was never set. */
function twoOption(v: boolean | number | null | undefined): boolean | undefined {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  return undefined;
}

function mapToDomain(entity: OpportunityEntity): Opportunity {
  return {
    id: entity.opportunityid,
    name: entity.name,
    // Relationships
    brokerId: entity._parentcontactid_value ?? undefined,
    brokerName:
      (entity['_parentcontactid_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    // Broker's agency lives on parentaccountid (the parent account of the
    // broker contact). Kept separate from accountName so the Group label and
    // the Broker label can be sourced independently.
    brokerAgencyId: entity._parentaccountid_value ?? undefined,
    brokerAgencyName:
      (entity['_parentaccountid_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    // customerid is the actual customer — for Veracity's B2B sales that's the
    // group account. Do NOT fall back to parentaccountid here; that would leak
    // the broker's agency into the group label when customerid is null.
    accountId: entity._customerid_value ?? undefined,
    accountName:
      (entity['_customerid_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    ownerId: entity._ownerid_value ?? undefined,
    ownerName:
      (entity['_ownerid_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    // Status
    stateCode: entity.statecode as OppStateCode,
    stateLabel:
      (entity['statecode@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    statusCode: entity.statuscode ?? undefined,
    statusLabel:
      (entity['statuscode@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    // Sales process
    stepName: entity.stepname ?? undefined,
    // Rating
    ratingCode: entity.opportunityratingcode ?? undefined,
    ratingLabel:
      (entity['opportunityratingcode@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    // Dates
    estimatedCloseDate: entity.estimatedclosedate ?? undefined,
    actualCloseDate: entity.actualclosedate ?? undefined,
    effectiveDate: entity.dts_effectivedate ?? undefined,
    createdOn: entity.createdon ?? undefined,
    modifiedOn: entity.modifiedon ?? undefined,
    // Group details
    numberOfEmployees: entity.dts_numberofemployees ?? undefined,
    numberOfMembers: entity.dts_numberofmembers ?? undefined,
    howInsured:
      (entity['dts_howinsured@OData.Community.Display.V1.FormattedValue'] as string)
      ?? entity.dts_howinsured
      ?? undefined,
    // Description & presentation status
    description: entity.description ?? undefined,
    presentationStatus:
      (entity['new_presentationstatus@OData.Community.Display.V1.FormattedValue'] as string)
      ?? undefined,
    // Win factors
    brokerOfRecord: twoOption(entity.dts_brokerofrecord),
    hasVeracityWonFromBroker: twoOption(entity.dts_hasveracitywonbusinessfrombroker),
    hasBrokerPlacedWithIndependent: twoOption(entity.dts_hasbrokereverplacedbusinesswithindependentt),
    // Renewal
    isRenewal: twoOption(entity.dts_renewal),
    // Current-vendor pickers — prefer FormattedValue (Choice label) and fall
    // back to the raw column for string-typed variants.
    currentTpa:
      (entity['dts_currenttpa@OData.Community.Display.V1.FormattedValue'] as string)
      ?? (typeof entity.dts_currenttpa === 'string' ? entity.dts_currenttpa : undefined),
    currentNetwork:
      (entity['dts_currentnetwork@OData.Community.Display.V1.FormattedValue'] as string)
      ?? (typeof entity.dts_currentnetwork === 'string' ? entity.dts_currentnetwork : undefined),
    currentPbm:
      (entity['dts_currentpbm@OData.Community.Display.V1.FormattedValue'] as string)
      ?? (typeof entity.dts_currentpbm === 'string' ? entity.dts_currentpbm : undefined),
    currentStopLoss:
      (entity['dts_currentstoploss@OData.Community.Display.V1.FormattedValue'] as string)
      ?? (typeof entity.dts_currentstoploss === 'string' ? entity.dts_currentstoploss : undefined),
    brokerCommissionType:
      (entity['dts_brokercommissiontype@OData.Community.Display.V1.FormattedValue'] as string)
      ?? (typeof entity.dts_brokercommissiontype === 'string' ? entity.dts_brokercommissiontype : undefined),
    // Products to Quote
    productsToQuote: buildProductsToQuote(entity),
    // Group
    groupId: entity._dts_group_value ?? undefined,
    groupName:
      (entity['_dts_group_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
  };
}

// =============================================================================
// Query Building
// =============================================================================

const SELECT_FIELDS = [
  'opportunityid', 'name',
  '_parentcontactid_value', '_customerid_value', '_parentaccountid_value', '_ownerid_value',
  'statecode', 'stepname',
  'opportunityratingcode',
  'estimatedclosedate', 'actualclosedate', 'dts_effectivedate',
  'createdon', 'modifiedon',
  'dts_numberofemployees', 'dts_howinsured',
  'new_presentationstatus',
  'dts_brokerofrecord', 'dts_hasveracitywonbusinessfrombroker',
  'dts_hasbrokereverplacedbusinesswithindependentt',
  'dts_renewal',
  'dts_currenttpa', 'dts_currentnetwork', 'dts_currentpbm', 'dts_currentstoploss',
  'dts_brokercommissiontype',
  'dts_tpaadmin', 'dts_veracityrx', 'dts_veracityonsite', 'dts_veracityrisk',
  'dts_providersofexcellence', 'dts_minimumessentialcoveragemec', 'dts_maplebridge',
  '_dts_group_value',
];

function buildQueryOptions(params: OpportunityQueryParams): QueryOptions {
  const options: QueryOptions = {
    select: SELECT_FIELDS,
    orderBy: 'createdon desc',
  };

  const filters: string[] = [];

  if (params.stateCode !== undefined) {
    filters.push(`statecode eq ${params.stateCode}`);
  }

  if (params.stages && params.stages.length > 0) {
    const stageFilter = params.stages
      .map((s) => `stepname eq '${escapeODataString(s)}'`)
      .join(' or ');
    filters.push(`(${stageFilter})`);
  }

  if (params.brokerId) {
    filters.push(`_parentcontactid_value eq '${assertGuid(params.brokerId, 'brokerId')}'`);
  }

  if (params.accountId) {
    filters.push(`_customerid_value eq '${assertGuid(params.accountId, 'accountId')}'`);
  }

  if (params.groupId) {
    filters.push(`_dts_group_value eq '${assertGuid(params.groupId, 'groupId')}'`);
  }

  if (params.ownerId) {
    filters.push(`_ownerid_value eq '${assertGuid(params.ownerId, 'ownerId')}'`);
  }

  if (params.nameFilter) {
    filters.push(`contains(name, '${escapeODataString(params.nameFilter)}')`);
  }

  if (params.closeDateFrom) {
    filters.push(`actualclosedate ge ${assertDateLiteral(params.closeDateFrom, 'closeDateFrom')}`);
  }
  if (params.closeDateTo) {
    filters.push(`actualclosedate le ${assertDateLiteral(params.closeDateTo, 'closeDateTo')}`);
  }

  if (params.effectiveDateFrom) {
    filters.push(`dts_effectivedate ge ${assertDateLiteral(params.effectiveDateFrom, 'effectiveDateFrom')}`);
  }
  if (params.effectiveDateTo) {
    filters.push(`dts_effectivedate le ${assertDateLiteral(params.effectiveDateTo, 'effectiveDateTo')}`);
  }

  if (filters.length > 0) {
    options.filter = filters.join(' and ');
  }

  return options;
}

// =============================================================================
// API Functions
// =============================================================================

async function listOpportunities(params: OpportunityQueryParams = {}): Promise<Opportunity[]> {
  const options = buildQueryOptions(params);
  const records = await dataverseClient.listAll<OpportunityEntity>('opportunities', options);
  return records.map(mapToDomain);
}

async function getOpportunity(id: string): Promise<Opportunity> {
  const options: QueryOptions = { select: SELECT_FIELDS };
  const entity = await dataverseClient.get<OpportunityEntity>('opportunities', id, options);
  return mapToDomain(entity);
}

async function getWonOpportunities(params: Omit<OpportunityQueryParams, 'stateCode'> = {}): Promise<Opportunity[]> {
  return listOpportunities({ ...params, stateCode: 1 });
}

async function getOpenOpportunities(params: Omit<OpportunityQueryParams, 'stateCode'> = {}): Promise<Opportunity[]> {
  return listOpportunities({ ...params, stateCode: 0 });
}

async function getPipelineOpportunities(
  stages: string[],
  params: Omit<OpportunityQueryParams, 'stateCode' | 'stages'> = {},
): Promise<Opportunity[]> {
  return listOpportunities({ ...params, stateCode: 0, stages });
}

/**
 * Product toggles the Lost Opportunities report asks for that no other page
 * uses (ADO #1449). Kept OUT of SELECT_FIELDS on purpose: an unknown column
 * name makes the whole opportunity query 400, and SELECT_FIELDS is shared by
 * every Sales, Ops, and Catalog page that reads opportunities. Isolating them
 * here means a tenant missing either column degrades one report instead of
 * blanking the module.
 */
const LOST_EXTRA_SELECT = ['new_caresolutions', 'dts_healthplansolutions'];

/**
 * Every Lost opportunity (statecode 2), with the two extra product toggles
 * when the tenant has them.
 *
 * Renewal filtering is deliberately NOT done here. `dts_renewal` is a display
 * column on this report as well as a filter, so callers get the full set and
 * decide; see `excludeRenewals` in ../model/types.
 */
async function getLostOpportunities(
  params: Omit<OpportunityQueryParams, 'stateCode'> = {},
): Promise<Opportunity[]> {
  const options = buildQueryOptions({ ...params, stateCode: 2 });
  try {
    const records = await dataverseClient.listAll<OpportunityEntity>('opportunities', {
      ...options,
      select: [...(options.select ?? SELECT_FIELDS), ...LOST_EXTRA_SELECT],
    });
    return records.map(mapToDomain);
  } catch (err) {
    console.warn(
      '[getLostOpportunities] Query with the Care Solutions / Health Plan Solutions '
      + 'toggles failed, retrying without them. Those two product columns will be '
      + 'blank on the report:',
      err,
    );
    const records = await dataverseClient.listAll<OpportunityEntity>('opportunities', options);
    return records.map(mapToDomain);
  }
}

// =============================================================================
// Opportunity Close
// =============================================================================

export interface OpportunityClose {
  id: string;
  opportunityId: string;
  actualRevenue?: number;
  closeDate?: string;
  description?: string;
  closureConfirmed?: boolean;
  wonOrLossReason?: string;
  secondaryCloseReason?: string;
  firstVeracitySaleForBroker?: boolean;
  confirmClientContact?: string;
  // Final Sales Information
  productsSold?: string;
  /** Raw numeric choice values (e.g. "780350000,780350001") — used for mapping */
  productsSoldRaw?: string;
  soldTpa?: string;
  tpaAdminFee?: string;
  pbmSold?: string;
  pharmacyFee?: string;
  feeConcession?: string;
  notesForImplementation?: string;
  // On the opportunity only (Sales Hub cutover 9/29); never on a close activity.
  networkSold?: string;
  stopLossCarrierSold?: string;
  stopLossPlacedBy?: string;
  planServicesSold?: string;
  cobraAdministration?: string;
  consumerDrivenProductsSold?: string;
  telemedicineVendorSold?: string;
  medicalAdminFeeSold?: string;
  medicalPlansSold?: string;
  concessionApprovedBy?: string;
  openEnrollmentStartDate?: string;
  openEnrollmentNotes?: string;
  outsideVendorsForImplementation?: string;
  /** Where the close-form fields came from; see lib/oppCloseFields.ts. */
  source?: 'opportunity' | 'closeActivity';
}

interface OpportunityCloseEntity {
  activityid: string;
  _opportunityid_value?: string;
  actualrevenue?: number;
  actualend?: string;
  description?: string;
  dts_closureconfirmation?: boolean;
  dts_statusclosereason?: number;
  'dts_statusclosereason@OData.Community.Display.V1.FormattedValue'?: string;
  dts_statusclosereasonsecondary?: number;
  'dts_statusclosereasonsecondary@OData.Community.Display.V1.FormattedValue'?: string;
  dts_firstveracitysaleforbroker?: boolean;
  // Confirm Client Contact is a Contact lookup on the close activity — the
  // entity-side property is `_dts_confirmclientcontacct_value` (note the
  // `contacct` double-c, that's how the column was named in Dynamics).
  _dts_confirmclientcontacct_value?: string;
  '_dts_confirmclientcontacct_value@OData.Community.Display.V1.FormattedValue'?: string;
  // Final Sales Information (multi-select Choice — raw is comma-separated ints, formatted is labels)
  dts_productssold?: string;
  'dts_productssold@OData.Community.Display.V1.FormattedValue'?: string;
  _dts_soldtpa_value?: string;
  '_dts_soldtpa_value@OData.Community.Display.V1.FormattedValue'?: string;
  // Fee columns are Currency / Decimal in Dataverse and come back as JS
  // numbers via the Web API — typed as `string | number` so the mapper can
  // coerce safely. feeConcession is freeform text in some tenants but defined
  // as a number in others; admit both to avoid future crashes.
  dts_tpaadminfee?: string | number;
  _dts_pbmsold_value?: string;
  '_dts_pbmsold_value@OData.Community.Display.V1.FormattedValue'?: string;
  dts_pharmacyfee?: string | number;
  dts_feeconcession?: string | number;
  dts_notesforimplementation?: string;
  [key: string]: unknown;
}

// Standard fields always available on opportunityclose
const CLOSE_SELECT_STANDARD = [
  'activityid', '_opportunityid_value',
  'actualrevenue', 'actualend', 'description', 'subject',
];

// Custom dts_ fields — discovered by probing; remove any that 400.
// Lookup columns use the `_<schemaname>_value` form so the formatted-value
// annotation comes back alongside the GUID.
const CLOSE_SELECT_CUSTOM = [
  'dts_closureconfirmation',
  'dts_statusclosereason',
  'dts_statusclosereasonsecondary',
  'dts_firstveracitysaleforbroker',
  '_dts_confirmclientcontacct_value',
  'dts_productssold',
  '_dts_soldtpa_value',
  'dts_tpaadminfee',
  '_dts_pbmsold_value',
  'dts_pharmacyfee',
  'dts_feeconcession',
  'dts_notesforimplementation',
];

const CLOSE_SELECT = [...CLOSE_SELECT_STANDARD, ...CLOSE_SELECT_CUSTOM];

/** Coerce a Dataverse scalar (which may be string OR number depending on the
 *  underlying column type) into a domain string. Returns undefined for null,
 *  undefined, or empty strings so downstream falsiness checks keep working. */
function toStringOrUndefined(v: string | number | null | undefined): string | undefined {
  if (v === null || v === undefined) return undefined;
  const s = typeof v === 'string' ? v : String(v);
  return s === '' ? undefined : s;
}

function mapCloseToDomain(entity: OpportunityCloseEntity): OpportunityClose {
  return {
    id: entity.activityid,
    opportunityId: entity._opportunityid_value ?? '',
    actualRevenue: entity.actualrevenue ?? undefined,
    closeDate: entity.actualend ?? undefined,
    description: entity.description ?? undefined,
    closureConfirmed: entity.dts_closureconfirmation ?? undefined,
    wonOrLossReason:
      (entity['dts_statusclosereason@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    secondaryCloseReason:
      (entity['dts_statusclosereasonsecondary@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    firstVeracitySaleForBroker: entity.dts_firstveracitysaleforbroker ?? undefined,
    confirmClientContact:
      (entity['_dts_confirmclientcontacct_value@OData.Community.Display.V1.FormattedValue'] as string)
      ?? undefined,
    // Final Sales Information
    productsSold:
      (entity['dts_productssold@OData.Community.Display.V1.FormattedValue'] as string)
      ?? entity.dts_productssold ?? undefined,
    productsSoldRaw: entity.dts_productssold ?? undefined,
    soldTpa:
      (entity['_dts_soldtpa_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    tpaAdminFee: toStringOrUndefined(entity.dts_tpaadminfee),
    pbmSold:
      (entity['_dts_pbmsold_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    pharmacyFee: toStringOrUndefined(entity.dts_pharmacyfee),
    feeConcession: toStringOrUndefined(entity.dts_feeconcession),
    notesForImplementation: entity.dts_notesforimplementation ?? undefined,
  };
}

/**
 * The close record for one opportunity. Fetches EVERY close activity on the
 * opp and picks via `resolveCloseRecord` (the #1449 rule): a reopened deal
 * leaves its re-close behind as an empty stub, and "newest actualend wins"
 * showed that stub on the Transition tab's Sales Close Form (Sasha 9/02)
 * while the original close held the data. Pass the opportunity's own
 * `actualclosedate` when the caller has it; without it the rule still
 * prefers a close carrying a description, then a confirmed one, then the
 * newest.
 */
async function getOpportunityClose(
  opportunityId: string,
  oppActualCloseDate?: string,
): Promise<OpportunityClose | null> {
  const [activity, fields] = await Promise.all([
    getCloseActivity(opportunityId, oppActualCloseDate),
    getOppCloseFields(opportunityId),
  ]);
  return overlayOppClose(opportunityId, activity, fields) ?? null;
}

/** Close information on the opportunity row itself; null when it has none. */
async function getOppCloseFields(opportunityId: string): Promise<OppCloseFields | null> {
  try {
    const response = await dataverseClient.list<OppCloseEntity>('opportunities', {
      select: [...OPP_CLOSE_SELECT],
      filter: `opportunityid eq '${assertGuid(opportunityId, 'opportunityId')}'`,
    });
    const row = response.value[0];
    return row ? oppCloseFields(row) : null;
  } catch (err) {
    // Pre-cutover org (columns missing): the activity is the only source.
    console.warn('[getOppCloseFields] opportunity close columns unavailable:', err);
    return null;
  }
}

/**
 * Close information for every closed opportunity that carries it on the row,
 * keyed by opportunity id. Empty on a pre-cutover org.
 */
async function getAllOppCloseFields(): Promise<Map<string, OppCloseFields>> {
  const map = new Map<string, OppCloseFields>();
  try {
    const rows = await dataverseClient.listAll<OppCloseEntity>('opportunities', {
      select: [...OPP_CLOSE_SELECT],
      filter: `statecode ne 0 and ${OPP_HAS_CLOSE_DATA_FILTER}`,
    });
    for (const row of rows) {
      const fields = oppCloseFields(row);
      if (fields) map.set(row.opportunityid, fields);
    }
  } catch (err) {
    console.warn('[getAllOppCloseFields] opportunity close columns unavailable:', err);
  }
  return map;
}

/** The #1449-resolved close ACTIVITY for one opportunity. */
async function getCloseActivity(
  opportunityId: string,
  oppActualCloseDate?: string,
): Promise<OpportunityClose | null> {
  const resolve = (entities: OpportunityCloseEntity[]): OpportunityClose | null => {
    const closes = entities.map(mapCloseToDomain);
    return resolveCloseRecord({ actualCloseDate: oppActualCloseDate }, closes) ?? null;
  };
  try {
    const response = await dataverseClient.list<OpportunityCloseEntity>('opportunitycloses', {
      select: CLOSE_SELECT,
      filter: `_opportunityid_value eq '${assertGuid(opportunityId, 'opportunityId')}'`,
      orderBy: 'actualend desc',
    });
    return resolve(response.value);
  } catch (err) {
    // Custom fields may not exist — retry with standard fields only
    console.warn('OpportunityClose full query failed, retrying with standard fields:', err);
    try {
      const response = await dataverseClient.list<OpportunityCloseEntity>('opportunitycloses', {
        select: ['activityid', '_opportunityid_value', 'actualrevenue', 'actualend', 'description'],
        filter: `_opportunityid_value eq '${assertGuid(opportunityId, 'opportunityId')}'`,
        orderBy: 'actualend desc',
      });
      return resolve(response.value);
    } catch {
      console.warn('OpportunityClose query failed entirely');
      return null;
    }
  }
}

export interface CloseReasonEntry {
  primary?: string;
  secondary?: string;
}

/**
 * Fetch close reasons for all closed opportunities in a single query.
 * Returns a Map from opportunityId → { primary, secondary } close reason
 * formatted strings. Most-recent close activity wins per opportunity.
 *
 * Tries primary + secondary in one query, falls back to primary-only if
 * the secondary field doesn't exist in this tenant.
 */
async function getCloseReasons(): Promise<Map<string, CloseReasonEntry>> {
  const baseSelect = ['activityid', '_opportunityid_value', 'dts_statusclosereason'];

  async function run(select: string[]): Promise<OpportunityCloseEntity[]> {
    return await dataverseClient.listAll<OpportunityCloseEntity>('opportunitycloses', {
      select,
      orderBy: 'actualend desc',
    });
  }

  let entities: OpportunityCloseEntity[] = [];
  let secondaryAvailable = true;
  try {
    entities = await run([...baseSelect, 'dts_statusclosereasonsecondary']);
  } catch (err) {
    console.warn(
      '[getCloseReasons] Query with secondary close reason failed — retrying without it. '
      + 'Field name may differ in this tenant. Error:',
      err,
    );
    secondaryAvailable = false;
    try {
      entities = await run(baseSelect);
    } catch (err2) {
      console.error('[getCloseReasons] Primary-only retry also failed:', err2);
      return new Map();
    }
  }

  const map = new Map<string, CloseReasonEntry>();
  for (const entity of entities) {
    const oppId = entity._opportunityid_value;
    if (!oppId || map.has(oppId)) continue;
    const primary = entity['dts_statusclosereason@OData.Community.Display.V1.FormattedValue'] as string | undefined;
    const secondary = secondaryAvailable
      ? (entity['dts_statusclosereasonsecondary@OData.Community.Display.V1.FormattedValue'] as string | undefined)
      : undefined;
    if (primary || secondary) {
      map.set(oppId, { primary, secondary });
    }
  }

  // The opportunity's own reasons win, record by record (lib/oppCloseFields.ts).
  for (const [oppId, f] of await getAllOppCloseFields()) {
    if (f.wonOrLossReason || f.secondaryCloseReason) {
      map.set(oppId, { primary: f.wonOrLossReason, secondary: f.secondaryCloseReason });
    } else {
      map.delete(oppId);
    }
  }

  if (!secondaryAvailable) {
    console.warn(
      '[getCloseReasons] Loaded %d opportunities with primary close reason only. '
      + 'Confirm the secondary-close-reason field name on the opportunityclose entity in Dataverse.',
      map.size,
    );
  }
  return map;
}

/**
 * Products Sold for every closed opportunity, in one bulk query.
 * Returns a Map from opportunityId to the close-form Choice labels.
 *
 * `dts_productssold` lives on the close activity, not the opportunity, so any
 * list that wants to show what was actually SOLD (rather than what was quoted
 * on the opportunity) has to join through here. Most-recent close wins per
 * opportunity, same rule as getCloseReasons.
 */
async function getProductsSold(): Promise<Map<string, string[]>> {
  let entities: OpportunityCloseEntity[] = [];
  try {
    entities = await dataverseClient.listAll<OpportunityCloseEntity>('opportunitycloses', {
      select: ['activityid', '_opportunityid_value', 'dts_productssold'],
      orderBy: 'actualend desc',
    });
  } catch (err) {
    // The field is a custom Choice column; if it is missing in this tenant the
    // whole query 400s. Degrade to "no sold data" rather than failing the page.
    console.warn('[getProductsSold] Query failed, no sold data available:', err);
    return new Map();
  }

  const map = new Map<string, string[]>();
  for (const entity of entities) {
    const oppId = entity._opportunityid_value;
    if (!oppId || map.has(oppId)) continue;
    const formatted =
      (entity['dts_productssold@OData.Community.Display.V1.FormattedValue'] as string | undefined)
      ?? undefined;
    if (!formatted) continue;
    const labels = formatted.split(/[,;]/).map((t) => t.trim()).filter(Boolean);
    if (labels.length > 0) map.set(oppId, labels);
  }
  // The opportunity's own Products Sold wins, record by record.
  for (const [oppId, f] of await getAllOppCloseFields()) {
    const labels = (f.productsSold ?? '').split(/[,;]/).map((t) => t.trim()).filter(Boolean);
    if (labels.length > 0) map.set(oppId, labels);
    else map.delete(oppId);
  }
  return map;
}

/**
 * EVERY close activity, grouped by opportunityId, in one bulk query.
 *
 * Distinct from `getCloseReasons`, which collapses to one record per
 * opportunity using "most recent `actualend` wins". About 1.5% of
 * opportunities carry more than one close (reopened then re-closed deals),
 * and for those the most-recent record is not always the one holding the
 * narrative. Callers that need the right record run `resolveCloseRecord`
 * over this map, which needs the opportunity's own `actualclosedate` and so
 * cannot be decided inside the query. (ADO #1449)
 *
 * The reason codes are NOT decoded here: Dataverse returns the choice labels
 * as FormattedValue annotations, so hardcoding an option-set crosswalk would
 * only go stale the first time someone adds a reason in Dynamics.
 */
async function getAllCloses(): Promise<Map<string, OpportunityClose[]>> {
  const baseSelect = [
    'activityid', '_opportunityid_value', 'actualend', 'description',
    'dts_closureconfirmation', 'dts_statusclosereason',
  ];

  let entities: OpportunityCloseEntity[] = [];
  try {
    entities = await dataverseClient.listAll<OpportunityCloseEntity>('opportunitycloses', {
      select: [...baseSelect, 'dts_statusclosereasonsecondary'],
      orderBy: 'actualend desc',
    });
  } catch (err) {
    console.warn(
      '[getAllCloses] Query with the secondary close reason failed, retrying without it:',
      err,
    );
    try {
      entities = await dataverseClient.listAll<OpportunityCloseEntity>('opportunitycloses', {
        select: baseSelect,
        orderBy: 'actualend desc',
      });
    } catch (err2) {
      console.error('[getAllCloses] Primary-only retry also failed:', err2);
      return new Map();
    }
  }

  const map = new Map<string, OpportunityClose[]>();
  for (const entity of entities) {
    const oppId = entity._opportunityid_value;
    if (!oppId) continue;
    const list = map.get(oppId);
    if (list) list.push(mapCloseToDomain(entity));
    else map.set(oppId, [mapCloseToDomain(entity)]);
  }
  // Opportunity-level close data overlays every activity of that opp, so the
  // #1449 resolver still picks the note while the fields come from the row.
  for (const [oppId, f] of await getAllOppCloseFields()) {
    const activities = map.get(oppId) ?? [];
    map.set(
      oppId,
      activities.length > 0
        ? activities.map((a) => overlayOppClose(oppId, a, f)!)
        : [overlayOppClose(oppId, undefined, f)!],
    );
  }
  return map;
}

// =============================================================================
// Opportunity Handoff (Client + Broker fields for the Ops Close Form)
// =============================================================================

/** A contact lookup on the opportunity, with the contact's phone and email. */
export interface OppHandoffContact {
  id: string;
  name?: string;
  phone?: string;
  email?: string;
}

/**
 * Summary-tab fields the implementation team needs at handoff (ADO #1724).
 * They live on the opportunity, not the close, and reps fill them before the
 * close, so they are read separately from `OpportunityClose` and never count
 * toward "this opportunity carries close data".
 */
export interface OppHandoff {
  opportunityId: string;
  numberOfEmployees?: string;
  numberOfMembers?: string;
  /** dts_howinsured choice label (Self-Funded, Fully Insured, ...). */
  howInsured?: string;
  /** dts_currenttpa: free text, the client's current administrator. */
  currentTpa?: string;
  broker?: OppHandoffContact;
  brokerAssistant?: OppHandoffContact;
  agency?: string;
  /** dts_brokercommissiontype choice label (PEPM, Flat Fee). */
  brokerCommissionType?: string;
  /** dts_brokercommissionamountrequested, currency-formatted by Dataverse. */
  brokerCommissionAmount?: string;
}

const OPP_HANDOFF_SELECT = [
  'opportunityid',
  'dts_numberofemployees',
  'dts_numberofmembers',
  'dts_howinsured',
  'dts_currenttpa',
  '_parentcontactid_value',
  '_dts_assistant_value',
  '_parentaccountid_value',
  'dts_brokercommissiontype',
  'dts_brokercommissionamountrequested',
];

const FV = '@OData.Community.Display.V1.FormattedValue';

/** Formatted value when Dataverse sends one (choice labels, lookup names,
 *  currency), else the raw scalar as a string. Blank reads as undefined. */
function formattedOrRaw(e: Record<string, unknown>, col: string): string | undefined {
  const f = e[`${col}${FV}`];
  if (typeof f === 'string' && f.trim()) return f;
  const v = e[col];
  if (v === null || v === undefined) return undefined;
  const s = String(v).trim();
  return s ? s : undefined;
}

interface ContactEntity {
  contactid: string;
  fullname?: string;
  telephone1?: string;
  emailaddress1?: string;
}

/**
 * Client + Broker handoff fields for one opportunity. The broker and the
 * assistant are contact lookups; their phone and email come from a second
 * contacts read by id rather than a $expand, so a custom lookup's navigation
 * property casing (dts_assistant vs dts_Assistant) can never 400 the card.
 * Returns null when the opportunity columns are unavailable.
 */
async function getOpportunityHandoff(opportunityId: string): Promise<OppHandoff | null> {
  let row: Record<string, unknown> | undefined;
  try {
    const response = await dataverseClient.list<OppCloseEntity>('opportunities', {
      select: OPP_HANDOFF_SELECT,
      filter: `opportunityid eq '${assertGuid(opportunityId, 'opportunityId')}'`,
    });
    row = response.value[0];
  } catch (err) {
    console.warn('[getOpportunityHandoff] handoff columns unavailable:', err);
    return null;
  }
  if (!row) return null;

  const lookup = (col: string): OppHandoffContact | undefined => {
    const id = row![col];
    if (typeof id !== 'string' || !id) return undefined;
    return { id, name: formattedOrRaw(row!, col) };
  };
  const broker = lookup('_parentcontactid_value');
  const brokerAssistant = lookup('_dts_assistant_value');

  const ids = [broker?.id, brokerAssistant?.id].filter((x): x is string => !!x);
  if (ids.length > 0) {
    try {
      const contacts = await dataverseClient.list<ContactEntity>('contacts', {
        select: ['contactid', 'fullname', 'telephone1', 'emailaddress1'],
        filter: ids.map((id) => `contactid eq '${assertGuid(id, 'contactId')}'`).join(' or '),
      });
      for (const c of contacts.value) {
        for (const target of [broker, brokerAssistant]) {
          if (target?.id !== c.contactid) continue;
          target.name = c.fullname?.trim() || target.name;
          target.phone = c.telephone1?.trim() || undefined;
          target.email = c.emailaddress1?.trim() || undefined;
        }
      }
    } catch (err) {
      // Names still come from the lookup annotations; only phone/email are lost.
      console.warn('[getOpportunityHandoff] broker contact read failed:', err);
    }
  }

  return {
    opportunityId,
    numberOfEmployees: formattedOrRaw(row, 'dts_numberofemployees'),
    numberOfMembers: formattedOrRaw(row, 'dts_numberofmembers'),
    howInsured: formattedOrRaw(row, 'dts_howinsured'),
    currentTpa: formattedOrRaw(row, 'dts_currenttpa'),
    broker,
    brokerAssistant,
    agency: formattedOrRaw(row, '_parentaccountid_value'),
    brokerCommissionType: formattedOrRaw(row, 'dts_brokercommissiontype'),
    brokerCommissionAmount: formattedOrRaw(row, 'dts_brokercommissionamountrequested'),
  };
}

// =============================================================================
// Opportunity Sales Team (connections)
// =============================================================================

export interface OppSalesTeamMember {
  /** systemuserid of the connected user. */
  userId: string;
  name?: string;
  /** Connection role label, e.g. "Sales Professional". */
  roleLabel?: string;
}

interface ConnectionEntity {
  connectionid: string;
  _record2id_value?: string;
  '_record2id_value@OData.Community.Display.V1.FormattedValue'?: string;
  record2objecttypecode?: number | string;
  'record2objecttypecode@OData.Community.Display.V1.FormattedValue'?: string;
  _record2roleid_value?: string;
  '_record2roleid_value@OData.Community.Display.V1.FormattedValue'?: string;
  [key: string]: unknown;
}

/** SystemUser entity type code — connections store the target table as a code. */
const SYSTEMUSER_TYPE_CODE = 8;

/**
 * Users on the opportunity's "Sales Team" subgrid. Dynamics backs that grid
 * with mirrored `connection` rows (User side carries the "Sales Professional"
 * role), so query only the opp→record2 direction and keep User-typed targets.
 * Contacts connected as Stakeholders etc. are filtered out.
 */
async function getSalesTeam(opportunityId: string): Promise<OppSalesTeamMember[]> {
  const response = await dataverseClient.list<ConnectionEntity>('connections', {
    select: ['connectionid', '_record2id_value', 'record2objecttypecode', '_record2roleid_value'],
    filter: `_record1id_value eq '${assertGuid(opportunityId, 'opportunityId')}'`,
  });
  const members = new Map<string, OppSalesTeamMember>();
  for (const c of response.value) {
    const isUser =
      c.record2objecttypecode === SYSTEMUSER_TYPE_CODE
      || c.record2objecttypecode === 'systemuser'
      || c['record2objecttypecode@OData.Community.Display.V1.FormattedValue'] === 'User';
    if (!isUser || !c._record2id_value || members.has(c._record2id_value)) continue;
    members.set(c._record2id_value, {
      userId: c._record2id_value,
      name: c['_record2id_value@OData.Community.Display.V1.FormattedValue'],
      roleLabel: c['_record2roleid_value@OData.Community.Display.V1.FormattedValue'],
    });
  }
  return [...members.values()];
}

// =============================================================================
// Rating change history (Dataverse audit)
// =============================================================================

export interface RatingChange {
  /** ISO timestamp of the change. */
  changedOn: string;
  /** Prior rating code (undefined when the rating was first set). */
  from?: number;
  /** New rating code after the change. */
  to?: number;
  /** Display name of the user who made the change, if available. */
  byName?: string;
}

const RATING_FIELD = 'opportunityratingcode';

interface AuditDetail {
  AuditRecord?: {
    createdon?: string;
    ['_userid_value@OData.Community.Display.V1.FormattedValue']?: string;
  };
  OldValue?: Record<string, unknown>;
  NewValue?: Record<string, unknown>;
}
interface RecordChangeHistoryResponse {
  AuditDetailCollection?: { AuditDetails?: AuditDetail[] };
}

/**
 * Rating-change timeline for one opportunity, read from Dataverse audit
 * history via the RetrieveRecordChangeHistory function. Relies on auditing
 * being enabled on the Opportunity table + Rating field (it has been for
 * months). Returns only the changes that touched `opportunityratingcode`,
 * sorted oldest → newest. Empty array = no recorded rating changes.
 */
async function getRatingHistory(opportunityId: string): Promise<RatingChange[]> {
  const id = assertGuid(opportunityId, 'opportunityId');
  const target = encodeURIComponent(`{'@odata.id':'opportunities(${id})'}`);
  const res = await dataverseClient.fetchRaw<RecordChangeHistoryResponse>(
    `RetrieveRecordChangeHistory(Target=@t)?@t=${target}`,
  );
  const details = res.AuditDetailCollection?.AuditDetails ?? [];
  const changes: RatingChange[] = [];
  for (const d of details) {
    const oldHas = !!d.OldValue && RATING_FIELD in d.OldValue;
    const newHas = !!d.NewValue && RATING_FIELD in d.NewValue;
    if (!oldHas && !newHas) continue;
    const changedOn = d.AuditRecord?.createdon;
    if (!changedOn) continue;
    changes.push({
      changedOn,
      from: oldHas ? (d.OldValue![RATING_FIELD] as number) : undefined,
      to: newHas ? (d.NewValue![RATING_FIELD] as number) : undefined,
      byName: d.AuditRecord?.['_userid_value@OData.Community.Display.V1.FormattedValue'],
    });
  }
  changes.sort((a, b) => a.changedOn.localeCompare(b.changedOn));
  return changes;
}

// =============================================================================
// Export
// =============================================================================

export const opportunityApi = {
  list: listOpportunities,
  get: getOpportunity,
  getWon: getWonOpportunities,
  getOpen: getOpenOpportunities,
  getPipeline: getPipelineOpportunities,
  getLost: getLostOpportunities,
  getClose: getOpportunityClose,
  getHandoff: getOpportunityHandoff,
  getCloseReasons,
  getAllCloses,
  getProductsSold,
  getRatingHistory,
  getSalesTeam,
};

// OpportunityClose is already exported at its interface declaration above
